"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.fetchSmartPricingRegionCheck = exports.applyStaticDepartureDays = exports.maxDefaultPrice = exports.toFloyd = exports.fromFloyd = exports.convertToCurrency = exports.splitPricingIntoPastAndCurrent = exports.splicePricingTogether = exports.newPricingPeriod = exports.humanizeDateFormat = exports.periodMinMaxPrices = exports.sortedPricingAndGaps = exports.getGap = exports.sortDates = exports.isDefaultFilled = exports.isPricingPeriod = void 0;
const diem_1 = __importDefault(require("@travelnest/diem"));
const helpers_1 = require("../../../../entities/pricing/helpers");
const ordinal_1 = __importDefault(require("ordinal"));
const lodash_1 = require("lodash");
const api_1 = __importDefault(require("../../../../api"));
const PRICING_PERIOD_DEFAULT_INITIAL_PRICE = 0;
const isPricingPeriod = (thing) => {
    if (typeof thing !== 'object')
        return false;
    return Object.keys(thing).includes('property_id');
};
exports.isPricingPeriod = isPricingPeriod;
/**
 * Did this pricingPeriod get generated by the lapsed pricing filler
 *   with data based on the property's default price per night?
 */
const isDefaultFilled = (pricingPeriod) => pricingPeriod && pricingPeriod.managed_by.includes('travelnest-lapse-filler');
exports.isDefaultFilled = isDefaultFilled;
const sortDates = (startDateA, startDateB) => {
    return new diem_1.default(startDateA).diff(new diem_1.default(startDateB));
};
exports.sortDates = sortDates;
/**
 * Get a pricing gap between 2 dates if it exists
 * @param prevEnd - ISO date string of previous period end (e.g. '2020-06-01')
 * @param nextStart - ISO date string of next period start (e.g. '2020-07-01')
 *
 * @example
 *
 * getGap('2020-06-15', '2020-06-30')
 * > { start_date: '2020-06-16', end_date: '2020-06-29'}
 */
const getGap = (prevEnd, nextStart) => {
    const dayPrevEnd = new diem_1.default(prevEnd);
    const dayNextStart = new diem_1.default(nextStart);
    // If there is a larger than 1 day gap between these dates
    if ((dayPrevEnd.diff(dayNextStart)) < -1) {
        // The actual gap is the day after prev end and before next start
        return {
            start_date: dayPrevEnd.setDate(dayPrevEnd.getDate() + 1).toISOString(),
            end_date: dayNextStart.setDate(dayNextStart.getDate() - 1).toISOString()
        };
    }
    else {
        return null;
    }
};
exports.getGap = getGap;
/**
 * Generate a sorted list of pricing, and any gaps that exist between the periods.
 * Note that this will generate a gap between today and the first pricing period if there is one.
 *
 * @param {IPricingUi[]} pricing - The list of pricing to sort and get gaps between.
 *
 * @returns {PricingWithGaps} sorted - The list of pricing, sorted and with gaps generated between periods.
 */
const sortedPricingAndGaps = (pricing) => {
    const sortedPricing = pricing.sort((a, b) => (0, exports.sortDates)(a.start_date, b.start_date));
    const gaps = [];
    if (!sortedPricing.length) {
        return [];
    }
    const todayToFirstPeriodGap = (0, exports.getGap)(new diem_1.default().toISOString(), sortedPricing[0].start_date);
    if (todayToFirstPeriodGap) {
        gaps.push(todayToFirstPeriodGap);
    }
    sortedPricing.forEach((curr, idx, arr) => {
        // Compare the end_date of current and start_date of next in array to find gaps
        const next = arr[idx + 1];
        if (!next) {
            return;
        }
        const gap = (0, exports.getGap)(curr.end_date, next.start_date);
        if (gap) {
            gaps.push(gap);
        }
    });
    return [...sortedPricing, ...gaps].sort((a, b) => (0, exports.sortDates)(a.start_date, b.start_date));
};
exports.sortedPricingAndGaps = sortedPricingAndGaps;
/**
 * Determine the min and max pricing in a given pricing period
 * @param {IPricingUi} p - The pricing period to determine prices for
 *
 * @returns {{ min: number, max: number }} prices - The min and max prices for that pricing period
 */
const periodMinMaxPrices = (p) => {
    const pricesOnly = Object.keys(p.prices).reduce((acc, curr) => {
        acc.push(p.prices[curr]);
        return acc;
    }, []);
    return { min: Math.min(...pricesOnly), max: Math.max(...pricesOnly) };
};
exports.periodMinMaxPrices = periodMinMaxPrices;
/**
 * Convert a date to a human-readable format
 * @param {string | Diem | Date} date - The date to convert
 */
const humanizeDateFormat = (date) => {
    const dateParts = new diem_1.default(date).toString().split(' ');
    // Remove day name
    dateParts.splice(0, 1);
    // Add ordinal to date
    dateParts[1] = (0, ordinal_1.default)(parseInt(dateParts[1], 10));
    return dateParts.join(' ');
};
exports.humanizeDateFormat = humanizeDateFormat;
/**
 * Generate a new pricing period
 * @param {number} propertyId - Property the pricing is for
 * @param {CurrencyCodes} currency - The currency to price in
 * @param {Diem} startDate - The date to start the pricing period on
 * @param {Diem} endDate - The date to end the period on (defaults to 30 days after start if omitted)
 * @param {number} price - The price to set each day at
 *
 * @returns {IPricingUi} The newly-created pricing period
 */
const newPricingPeriod = (propertyId, currency, startDate, endDate, price = PRICING_PERIOD_DEFAULT_INITIAL_PRICE) => {
    return {
        property_id: propertyId.toString(),
        name: 'New Pricing Period',
        start_date: startDate.toISOString(),
        end_date: endDate ? endDate.toISOString() : new diem_1.default(startDate).setDate(startDate.getDate() + 30).toISOString(),
        min_stay: 1,
        seven_night_discount: 0,
        arrival_days: { mon: true, tue: true, wed: true, thu: true, fri: true, sat: true, sun: true },
        departure_days: { mon: true, tue: true, wed: true, thu: true, fri: true, sat: true, sun: true },
        prices: { mon: price, tue: price, wed: price, thu: price, fri: price, sat: price, sun: price },
        managed_by: 'portal',
        currency,
    };
};
exports.newPricingPeriod = newPricingPeriod;
/**
 * Combine two arrays of pricing together. Behaves like the spread operator and prefers new pricing
 * where any overlaps exist.
 *
 * @param {IPricingUi[]} oldPricing - The list of pricing to begin with.
 * @param {IPricingUi[]} newPricing - The new pricing to splice in.
 * @returns {IPricingUi[]} spliced - The 2 arrays of pricing spliced together
 */
const splicePricingTogether = (oldPricing, newPricing) => {
    if (!oldPricing.length) {
        return [...newPricing];
    }
    if (!newPricing.length) {
        return [...oldPricing];
    }
    const oldSorted = oldPricing.sort((a, b) => (0, exports.sortDates)(a.start_date, b.start_date));
    const newSorted = newPricing.sort((a, b) => (0, exports.sortDates)(a.start_date, b.start_date));
    const newFirstStartDate = new diem_1.default(newSorted[0].start_date);
    // 0 indicates same day
    // >0 indicates end_date is after newFirstStartDate
    const firstOldOverlapIdx = oldSorted.findIndex((p) => new diem_1.default(p.end_date).diff(newFirstStartDate) >= 0);
    if (firstOldOverlapIdx >= 0) {
        // Has overlap
        return [...oldSorted.slice(0, firstOldOverlapIdx), ...newSorted];
    }
    else {
        // No overlap
        return [...oldSorted, ...newSorted];
    }
};
exports.splicePricingTogether = splicePricingTogether;
/**
 * Separate pricing into previous and current using today's date.
 *
 * @param {IPricingUi[]} pricing - The pricing to split
 *
 * @returns {{ current: IPricingUi[], past: IPricingUi[] }} - The pricing split into current and past
 */
const splitPricingIntoPastAndCurrent = (pricing) => {
    const current = [];
    const past = [];
    (pricing || []).forEach((p) => {
        // end_date is before or on today's date
        if (new diem_1.default(p.end_date).diff(new diem_1.default()) <= 0) {
            past.push(p);
        }
        else {
            current.push(p);
        }
    });
    return { current, past };
};
exports.splitPricingIntoPastAndCurrent = splitPricingIntoPastAndCurrent;
/**
 * Convert a value from GBP to another currency.
 * @param {number} valueGbp - The pounds value to convert
 * @param {CurrencyCodes} to - The currency to convert to
 * @param {FxRatesOutput} fxRates - The FX rates we have
 *
 * @returns {number | undefined} value - If we have a rate for that currency, this will be the value converted.
 */
const convertToCurrency = (valueGbp, to, fxRates) => {
    if (to === helpers_1.CurrencyCodes.GBP) {
        return parseInt(valueGbp.toFixed(0), 10);
    }
    const conversionSymbol = `${helpers_1.CurrencyCodes.GBP}:${to}`;
    const rate = fxRates[conversionSymbol];
    if (!rate) {
        return undefined;
    }
    const result = valueGbp * rate;
    return parseInt(result.toFixed(0), 10);
};
exports.convertToCurrency = convertToCurrency;
// Default prices are stored as Floyd, $123 is stored as 1230000, therefore, we want to translate it from Floyd when displaying in TBP..
const fromFloyd = (floydPrice) => (Number(floydPrice) || 0) / 10000;
exports.fromFloyd = fromFloyd;
// ...and convert back to Floyd when saving on Pricing Srvice
const toFloyd = (price) => (Number(price) || 0) * 10000;
exports.toFloyd = toFloyd;
exports.maxDefaultPrice = 999999;
const applyStaticDepartureDays = (pricingPeriods) => pricingPeriods.map((period) => (Object.assign(Object.assign({}, period), { departure_days: (0, helpers_1.allDaysAllowed)() })));
exports.applyStaticDepartureDays = applyStaticDepartureDays;
/**
 * A memoized function to determine Smart Pricing region support via our API.
 *
 * Uses lodash.memoize https://lodash.com/docs/4.17.15#memoize.
 *
 * Note the 2nd arg sets the cache key to the array.join result of all the inputs.
 *
 */
exports.fetchSmartPricingRegionCheck = (0, lodash_1.memoize)((propertyId, latitude, longitude) => {
    return api_1.default.smartPricing.checkRegionSupport(propertyId, latitude, longitude);
}, (...args) => args.join());
//# sourceMappingURL=helpers.js.map